import { useCallback } from 'react';
import { DataListColumn, DataListWebhookFieldMapping, DataListWebhookFieldMappingMode } from '../../services/api';

export interface FieldMatcherProps {
  /** Flattened dot-paths detected from a tested webhook sample (flattenSampleFields). */
  sourceFields: string[];
  targetColumns: DataListColumn[];
  value: DataListWebhookFieldMapping[];
  onChange: (next: DataListWebhookFieldMapping[]) => void;
  /** columnsSource === 'system' — gates the isExternalId pill (Field mode only). */
  showExternalId: boolean;
  /** systemModule === 'orders' — gates the contact-id/contact-identifier
   * pills (Field mode only). */
  showOrderRelationPickers?: boolean;
  /** systemModule === 'orders' AND Items Mapping's arrayPath is blank — gates
   * the product-SKU pill specifically. Kept separate from
   * showOrderRelationPickers because the SKU pill maps the exact same "which
   * field is the product SKU" concept as Items Mapping's skuPath; showing
   * both on screen at once when Items Mapping is configured let a list
   * define the SKU two different, silently-conflicting ways. Contact
   * pickers have no such overlap, so they stay gated by
   * showOrderRelationPickers alone regardless of Items Mapping. */
  showProductSkuPicker?: boolean;
}

/**
 * Mapping-array mutation logic for FieldMatcher.tsx — kept in its own file so
 * the "build next array, preserve other fields on a mode switch" logic is
 * easy to unit-test independent of the component's rendering.
 */
export const useFieldMappingState = (value: DataListWebhookFieldMapping[], onChange: (next: DataListWebhookFieldMapping[]) => void) => {
  const getMapping = useCallback((columnId: string) => value.find((m) => m.columnId === columnId), [value]);

  const upsert = useCallback(
    (columnId: string, patch: Partial<DataListWebhookFieldMapping>) => {
      const existing = value.find((m) => m.columnId === columnId);
      const next: DataListWebhookFieldMapping = {
        columnId,
        sourcePath: '',
        mode: 'field',
        ...existing,
        ...patch,
      };
      onChange([...value.filter((m) => m.columnId !== columnId), next]);
    },
    [value, onChange]
  );

  const remove = useCallback(
    (columnId: string) => {
      onChange(value.filter((m) => m.columnId !== columnId));
    },
    [value, onChange]
  );

  /** Drag-connect (web) or picker-select (native) — always wins, switches to Field mode. */
  const setFieldPath = useCallback(
    (columnId: string, sourcePath: string) => {
      upsert(columnId, { sourcePath, mode: 'field', fixedValue: undefined, expression: undefined });
    },
    [upsert]
  );

  const setMode = useCallback(
    (columnId: string, mode: DataListWebhookFieldMappingMode) => {
      const existing = getMapping(columnId);
      const relationFlags = { isExternalId: false, isProductSku: false, isContactId: false, isContactIdentifier: false };
      if (mode === 'field') {
        upsert(columnId, { mode, fixedValue: undefined, expression: undefined });
      } else if (mode === 'fixed') {
        upsert(columnId, { mode, sourcePath: '', fixedValue: existing?.fixedValue || '', expression: undefined, ...relationFlags });
      } else {
        upsert(columnId, { mode, sourcePath: '', fixedValue: undefined, expression: existing?.expression || '', ...relationFlags });
      }
    },
    [upsert, getMapping]
  );

  const setFixedValue = useCallback(
    (columnId: string, fixedValue: string) => upsert(columnId, { mode: 'fixed', fixedValue, sourcePath: '', expression: undefined }),
    [upsert]
  );

  const setExpression = useCallback(
    (columnId: string, expression: string) => upsert(columnId, { mode: 'expression', expression, sourcePath: '', fixedValue: undefined }),
    [upsert]
  );

  /** isExternalId is exclusive across the whole mapping array, and restricted to Field mode. */
  const setExternalId = useCallback(
    (columnId: string, isExternalId: boolean) => {
      const hasEntry = value.some((m) => m.columnId === columnId);
      const withFlag = hasEntry
        ? value.map((m) => ({ ...m, isExternalId: m.columnId === columnId ? isExternalId : false }))
        : [...value.map((m) => ({ ...m, isExternalId: false })), { columnId, sourcePath: '', mode: 'field' as const, isExternalId }];
      onChange(withFlag);
    },
    [value, onChange]
  );

  /** isProductSku is exclusive across the whole mapping array, restricted to Field mode. */
  const setProductSkuField = useCallback(
    (columnId: string, isProductSku: boolean) => {
      const hasEntry = value.some((m) => m.columnId === columnId);
      const withFlag = hasEntry
        ? value.map((m) => ({ ...m, isProductSku: m.columnId === columnId ? isProductSku : false }))
        : [...value.map((m) => ({ ...m, isProductSku: false })), { columnId, sourcePath: '', mode: 'field' as const, isProductSku }];
      onChange(withFlag);
    },
    [value, onChange]
  );

  /** isContactId is exclusive across the whole mapping array, restricted to Field mode. */
  const setContactIdField = useCallback(
    (columnId: string, isContactId: boolean) => {
      const hasEntry = value.some((m) => m.columnId === columnId);
      const withFlag = hasEntry
        ? value.map((m) => ({ ...m, isContactId: m.columnId === columnId ? isContactId : false }))
        : [...value.map((m) => ({ ...m, isContactId: false })), { columnId, sourcePath: '', mode: 'field' as const, isContactId }];
      onChange(withFlag);
    },
    [value, onChange]
  );

  /** isContactIdentifier is exclusive across the whole mapping array, restricted to Field mode. */
  const setContactIdentifierField = useCallback(
    (columnId: string, isContactIdentifier: boolean) => {
      const hasEntry = value.some((m) => m.columnId === columnId);
      const withFlag = hasEntry
        ? value.map((m) => ({ ...m, isContactIdentifier: m.columnId === columnId ? isContactIdentifier : false }))
        : [...value.map((m) => ({ ...m, isContactIdentifier: false })), { columnId, sourcePath: '', mode: 'field' as const, isContactIdentifier }];
      onChange(withFlag);
    },
    [value, onChange]
  );

  return {
    getMapping, upsert, remove, setFieldPath, setMode, setFixedValue, setExpression,
    setExternalId, setProductSkuField, setContactIdField, setContactIdentifierField,
  };
};
